import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import config from '@/config'

// ─── Types ──────────────────────────────────────────────────────────────────

export interface EveCompany {
  id: string
  name: string
  stampede_org_id?: string
  status?: string
  owner_name?: string
  owner_email?: string
  arr_pence?: number
  mrr_pence?: number
  venue_count?: number
  is_live?: boolean
  created_at?: string
  [key: string]: unknown
}

export interface EveSubscription {
  id: string
  stripe_subscription_id?: string
  status?: string
  mrr_pence?: number
  currency?: string
  current_period_start?: string
  current_period_end?: string
  cancel_at_period_end?: boolean
  [key: string]: unknown
}

export interface EveMrrMovement {
  id: string
  company_id: string
  movement_type?: string
  delta_pence?: number
  new_mrr_pence?: number
  effective_date?: string
  [key: string]: unknown
}

export interface EveHealthScore {
  id: string
  company_id: string
  score?: number
  status?: string
  calculated_at?: string
  [key: string]: unknown
}

export interface EveDeal {
  id: string
  company_id?: string
  deal_name?: string
  stage?: string
  amount?: number
  close_date?: string
  owner_name?: string
  [key: string]: unknown
}

export interface EveSupportTicket {
  id: string
  company_id?: string
  subject?: string
  status?: string
  priority?: string
  created_at?: string
  [key: string]: unknown
}

export interface EveCsatScore {
  id: string
  company_id?: string
  score?: number
  created_at?: string
  [key: string]: unknown
}

export interface EveAccountAllResponse {
  data: {
    company: EveCompany
    venues: unknown[]
    contacts: unknown[]
    tech_stack: unknown
    product_users: unknown[]
    revenue: {
      subscriptions: EveSubscription[]
      invoices: unknown[]
      xero_invoices: unknown[]
      stripe_connect_accounts: unknown[]
      mrr_movements: EveMrrMovement[]
      marketing_spend: unknown[]
    }
    crm: {
      deals: EveDeal[]
      deal_stage_history: unknown[]
      deal_activities: unknown[]
      commissions: unknown[]
      hubspot_quotes: unknown[]
    }
    health: {
      scores: EveHealthScore[]
      alerts: unknown[]
      dunning_records: unknown[]
      dunning_calls: unknown[]
      churn_records: unknown[]
      churn_risk_signals: unknown[]
      cancellation_notices: unknown[]
    }
    support: {
      tickets: EveSupportTicket[]
      call_activity: unknown[]
      survey_responses: unknown[]
      survey_tokens: unknown[]
      csat_scores: EveCsatScore[]
    }
    product: {
      activity_snapshots: unknown[]
      consumption: unknown[]
      venue_consumption: unknown[]
      module_stats: unknown[]
    }
    hardware: {
      orders: unknown[]
      order_items: unknown[]
      devices: unknown[]
      controller_sites: unknown[]
    }
    enrichment: {
      company_enrichment: unknown[]
      intent_signals: unknown[]
    }
    arr: {
      anchor_customers: unknown[]
      mapping_queue: unknown[]
    }
  }
}

export interface EveFinancialHistoryResponse {
  company: EveCompany
  active_mrr_pence: number
  subscriptions: EveSubscription[]
  timeline: {
    month: string
    mrr_pence: number
    invoices: unknown[]
  }[]
  fiscal_years: unknown[]
  raw_movements: EveMrrMovement[]
  raw_invoices: unknown[]
}

export interface EveDataResponse<T = unknown> {
  data: T[]
  total: number
  offset: number
  limit: number
}

export interface EveEngagementResponse {
  org_id: string
  daily_activity: { date: string; count: number }[]
  active_users: number
  top_features: { feature: string; count: number }[]
  top_pages: { page: string; count: number }[]
}

// ─── API ────────────────────────────────────────────────────────────────────

const eveApi = createApi({
  reducerPath: 'eve',
  baseQuery: fetchBaseQuery({
    baseUrl: config.url.eve,
    credentials: 'include',
  }),
  tagTypes: ['EveAccount'],
  endpoints: (build) => ({
    /** Returns every data point in Oracle for a Stampede organisation */
    getAccountAll: build.query<EveAccountAllResponse, string>({
      query: (orgId) => `/api/accounts/${orgId}/all`,
      providesTags: (_r, _e, orgId) => [{ type: 'EveAccount', id: orgId }],
    }),

    /** Per-month financial history for a single account */
    getFinancialHistory: build.query<EveFinancialHistoryResponse, string>({
      query: (companyId) => ({
        url: '/api/accounts/financial-history',
        params: { company_id: companyId },
      }),
    }),

    /** Product engagement metrics for an org (30 days) */
    getEngagement: build.query<EveEngagementResponse, string>({
      query: (orgId) => ({
        url: '/api/engagement/account',
        params: { org_id: orgId },
      }),
    }),

    /** Generic paginated table read */
    getData: build.query<
      EveDataResponse,
      {
        table: string
        offset?: number
        limit?: number
        sort?: string
        dir?: 'asc' | 'desc'
      }
    >({
      query: ({ table, offset = 0, limit = 50, sort, dir }) => ({
        url: '/api/data',
        params: { table, offset, limit, sort, dir },
      }),
    }),

    /** Search companies by name */
    searchCompanies: build.query<
      { active: EveCompany[]; dunning: EveCompany[]; dormant: EveCompany[] },
      { q: string; limit?: number }
    >({
      query: ({ q, limit = 10 }) => ({
        url: '/api/mapping/search',
        params: { q, limit },
      }),
    }),
  }),
})

export const {
  useGetAccountAllQuery,
  useGetFinancialHistoryQuery,
  useGetEngagementQuery,
  useGetDataQuery,
  useSearchCompaniesQuery,
} = eveApi

export default eveApi
